import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import {
  stringToBool, stringToNumber, buildClassAttrib, stripQuotes
} from '../../utils/ids-string-utils/ids-string-utils';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsOrientationMixin from '../../mixins/ids-orientation-mixin/ids-orientation-mixin';
import IdsHideFocusMixin from '../../mixins/ids-hide-focus-mixin/ids-hide-focus-mixin';
import IdsElement from '../../core/ids-element';

import '../ids-text/ids-text';
import '../ids-icon/ids-icon';
import '../ids-badge/ids-badge';

import styles from './ids-tab.scss';
import type IdsText from '../ids-text/ids-text';
import type IdsSeparator from '../ids-separator/ids-separator';
import type IdsBadge from '../ids-badge/ids-badge';
import IdsTabContent from './ids-tab-content';
import IdsInput from '../ids-input/ids-input';
import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import type IdsTabGroup from './ids-tab-group';

type IdsTabOnActionCallback = (isSelected: boolean) => void;

const Base = IdsDirtyTrackerMixin(
  IdsLocaleMixin(
    IdsKeyboardMixin(
      IdsColorVariantMixin(
        IdsOrientationMixin(
          IdsHideFocusMixin(
            IdsEventsMixin(
              IdsElement
            )
          )
        )
      )
    )
  )
);

/**
 * IDS Tab Component
 * @type {IdsTab}
 * @inherits IdsElement
 * @mixes IdsColorVariantMixin
 * @mixes IdsOrientationMixin
 * @mixes IdsHideFocusMixin
 * @mixes IdsEventsMixin
 * @part container - the tab container itself
 */
@customElement('ids-tab')
@scss(styles)
export default class IdsTab extends Base {
  /**
   * @param {IdsTabOnActionCallback} onAction a user-defined callback function that can be applied to a Tab
   */
  onAction?: IdsTabOnActionCallback;

  constructor() {
    super();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array<string>} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.ACTIONABLE,
      attributes.COUNT,
      attributes.DISMISSIBLE,
      attributes.DISABLED,
      attributes.DISABLE_ERROR,
      attributes.EDITABLE,
      attributes.HIDDEN,
      attributes.LABEL,
      attributes.SELECTED,
      attributes.SUB_TABS,
      attributes.VALUE,
      attributes.HAS_ERROR,
      attributes.MAX_WIDTH,
      attributes.MIN_WIDTH,
      attributes.TOOLTIP
    ];
  }

  /**
   * Inherited from `IdsColorVariantMixin`
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = ['alternate', 'module', 'header'];

  /**
   * @returns {Array<string>} Drawer vetoable events
   */
  vetoableEventTypes = [
    'beforetabremove',
  ];

  /**
   * Create the Template for the contents
   * @returns {string} the template to render
   */
  template(): string {
    const hasIcon = this.querySelector('ids-icon:not([slot="error"])');
    const hasCount = this.hasAttribute(attributes.COUNT);
    const hasSubTabs = this.hasAttribute(attributes.SUB_TABS);
    const hasTooltip = this.hasAttribute(attributes.TOOLTIP);
    const hasMoreOptionsSlot = this.querySelector('[slot="more-options"]');
    const cssClassAttr = buildClassAttrib(
      'ids-tab',
      `${hasSubTabs ? 'has-sub-tabs' : ''}`,
      this.selected,
      this.orientation ? `orientation-${this.orientation}` : '',
      this.count,
      `${hasMoreOptionsSlot ? 'has-more-options' : ''}`
    );
    const selectedAttr = this.selected ? ' font-weight="semi-bold"' : '';

    const moreOptionsTemplate = hasMoreOptionsSlot ? `<ids-menu-button hidden tabIndex="0" height="24px" width="24px" id="more-options-button-${this.value}" class="more-options-btn" menu="more-options-menu-${this.value}" icon="caret-down"></ids-menu-button>
      <ids-popup-menu align-y="bottom" id="more-options-menu-${this.value}" class="more-options" target="#more-options-button-${this.value}">
        <slot name="more-options"></slot>
      </ids-popup-menu> ` : '';

    let innerContent = '<slot></slot>';
    if (!hasIcon && !hasCount) {
      innerContent = `<ids-text overflow="ellipsis" ${hasTooltip && this.editable ? `tooltip="true"` : ''} size="22"${selectedAttr}>
      ${!this.label ? '<slot></slot>' : `${this.label} `}
      </ids-text>`;
    } else if (hasCount) {
      innerContent = `<div class="ids-tab-count-container">
        <ids-text class="ids-tab-count" overflow="ellipsis" font-size="28"${selectedAttr}>
          ${this.getAttribute(attributes.COUNT)}
        </ids-text>
        <ids-text overflow="ellipsis" size="22" tooltip="true">
          ${!this.label ? '<slot></slot>' : `${this.label} `}
        </ids-text>
      </div>`;
    }

    let subMenus = ``;
    const subTabs = this.querySelector('ids-sub-tabs');
    if (hasSubTabs && subTabs) {
      const subTabList = subTabs?.children;

      let subTabTemplate = ``;
      if (subTabList) {
        for (let i = 0; i < subTabList.length; i++) {
          const subTab = subTabList[i];
          const itemValue = subTab.getAttribute('value');
          let subTabAttrTemplate = ``;
          for (let j = 0; j < subTab.attributes.length; j++) {
            const subAttr = subTab.attributes[j];
            if (subAttr.name !== 'value') {
              if (subAttr.value !== '') subTabAttrTemplate += `${subAttr.name}="${subAttr.value}" `;
              else subTabAttrTemplate += `${subAttr.name} `;
            }
          }
          subTabTemplate += `<ids-sub-tab value="${itemValue}" ${subTabAttrTemplate.trimEnd()}>${subTab.innerHTML.trim()}</ids-sub-tab>`;
        }
      }
      subTabs?.remove();

      subMenus = `<ids-menu-button id="menu-btn" menu="menu" appearance="icon" icon="dropdown"></ids-menu-button>
      <ids-popup-menu id="menu" target="menu-btn">
        <ids-sub-tabs>
          ${subTabTemplate}
        </ids-sub-tabs>
      </ids-popup-menu>`;
    }

    const isStandardTab = (this.value !== 'reset' && this.value !== 'add');
    const isModuleTab = (this.parentNode as HTMLElement)?.getAttribute('color-variant') === 'module';

    return `<div ${cssClassAttr} part="container">
      ${isStandardTab && this.notificationCount ? `<ids-badge color="inactive" class="tab-badge">${this.notificationCount}</ids-badge>` : ''}
      ${innerContent}
      ${subMenus}
      <slot name="error">
        <ids-icon icon="error" color="error" size="small"></ids-icon>
      </slot>
      <div class="tab-action-container">
        ${isStandardTab && isModuleTab ? '<ids-separator vertical="true" hidden></ids-separator>' : ''}
        ${moreOptionsTemplate}
        <div class="ids-tab-trigger-container">
          <slot name="close"></slot>
          <slot name="update"></slot>
          <slot name="cancel"></slot>
        </div>
      </div>
    </div>
    ${this.label ? '<div class="tab-content"><slot></slot></div>' : ''}`;
  }

  /**
   * Generates HTML templates for tab action buttons.
   * Creates trigger buttons with appropriate styling and icons for different actions.
   * @param {string} type - The type of button to generate ('dismissible', 'update', 'cancel')
   * @returns {string} HTML string for the requested button template
   */
  private buttonTemplates(type: string = 'dismissible'): string {
    const colorVariant = `color-variant="${this.#getDismissibleVariant()}"`;
    const inheritColor = this.colorVariant === 'module' ? '' : 'inherit-color';
    const tabsButton: any = {
      dismissible: () => `<ids-trigger-button class="ids-tab-close-button" slot="close" label="Close" tabIndex="0" ${colorVariant} ${inheritColor}>
          <ids-icon icon="close" size="small"></ids-icon>
        </ids-trigger-button>`,
      update: () => `<ids-trigger-button class="ids-tab-update-button" slot="update" label="Update" tabIndex="0" ${colorVariant} ${inheritColor}>
          <ids-icon icon="check" size="small"></ids-icon>
        </ids-trigger-button>`,
      cancel: () => `<ids-trigger-button class="ids-tab-cancel-button" slot="cancel" label="Cancel" tabIndex="0" ${colorVariant} ${inheritColor}>
          <ids-icon icon="close" size="small"></ids-icon>
        </ids-trigger-button>`,
    };

    return tabsButton[type] ? tabsButton[type]() : '';
  }

  #getDismissibleVariant() {
    return this.colorVariant === 'alternate' ? 'alternate' : 'module';
  }

  /**
   * Gets the elements assigned to the tab content slot
   * @returns {Element[]} Array of elements in the tab content slot
   */
  get tabContents(): Element[] {
    const slot = this.shadowRoot?.querySelector('.tab-content slot') as HTMLSlotElement;
    return slot?.assignedElements() || [];
  }

  /**
   * Gets the parent tab group element
   * @returns {IdsTabGroup} The parent tab group
   */
  get tabGroup(): IdsTabGroup {
    return this.parentElement as IdsTabGroup;
  }

  get lazy(): boolean {
    return this.hasAttribute(attributes.LAZY);
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute(htmlAttributes.ROLE, 'tab');
    this.setAttribute(htmlAttributes.ARIA_SELECTED, `${Boolean(this.selected)}`);
    this.setAttribute(htmlAttributes.TABINDEX, stringToBool(this.selected) ? '0' : '-1');
    this.setAttribute(htmlAttributes.ARIA_LABEL, this.#getReadableAriaLabel());
    this.setAriaControls();
    this.#detectSwappable();
    this.#attachEventHandlers();
    this.#afterConnectedCallback();
    this.#attachKeyboardListeners();
  }

  #attachKeyboardListeners() {
    this.listen('Escape', this, () => {
      this.endEdit(false);
    });
  }

  /**
   * When a child value or this component value changes,
   * called to rebind onclick callbacks to each child
   * @returns {void}
   */
  #attachEventHandlers() {
    const popupMenu = this.shadowRoot?.querySelector('ids-popup-menu');
    this.offEvent('click.tabs-popup');
    this.onEvent('click.tabs-popup', popupMenu, (e: any) => {
      const thisItem = e.target.closest('ids-sub-tab');
      if (thisItem) {
        this.triggerEvent('tabselect', this, {
          bubbles: true,
          detail: {
            selected: true,
            value: thisItem.value
          }
        });
      }
    });

    // Add double click to the container
    const text = this.tabTextElement;
    if (this.editable && text) {
      this.offEvent('dblclick', text);
      this.onEvent('dblclick', text, () => {
        this.editTabName();
      });
    }

    if (this.menu) {
      this.offEvent('selected', this.menu);
      this.onEvent('selected', this.menu, (e: CustomEvent) => {
        const event = new CustomEvent('selected.tab-option', {
          bubbles: true,
          detail: {
            elem: this,
            source: e,
            option: e.detail?.elem?.value,
            tab: this.value
          }
        });
        this.parentElement?.dispatchEvent(event);
      });
    }
  }

  /**
   * Enables inline editing mode for the tab name.
   * Replaces the tab text with an input field and shows update/cancel buttons.
   * @returns {void}
   */
  editTabName() {
    const text = this.tabTextElement;

    if (!text || this.disabled) return;

    const input = document.createElement('ids-input') as IdsInput;

    const dismissisedBtn = this.shadowRoot?.querySelector('.ids-tab .ids-tab-trigger-container slot[name="close"]') as HTMLElement;
    if (dismissisedBtn) {
      dismissisedBtn.toggleAttribute(attributes.HIDDEN);
    }

    input.classList.add('tab-name-editor');
    input.setAttribute(attributes.FIELD_HEIGHT, 'xs');
    input.input!.tabIndex = 0;
    const tabTextContent = text?.textContent?.trim() || stripQuotes(text.shadowRoot?.querySelector('[part="text"]')?.getAttribute('data-text')?.trim() || '');

    input.value = tabTextContent || '';

    let width = this.calculateTextWidth(tabTextContent);
    input.style.setProperty('--text-width', `${width}px`);

    this.insertAdjacentHTML('beforeend', this.buttonTemplates('cancel'));
    this.insertAdjacentHTML('beforeend', this.buttonTemplates('update'));
    text.insertAdjacentElement('afterend', input);

    this.toggleAttribute(attributes.EDITING);
    text.toggleAttribute(attributes.HIDDEN);

    input.offEvent('input', input);
    input.onEvent('input', input, () => {
      width = this.calculateTextWidth(input.value);
      input.style.setProperty('--text-width', `${width}px`);
    });
    input.focus();

    const event = new CustomEvent('tab.startedit', {
      bubbles: true,
      detail: {
        elem: this,
        value: tabTextContent,
        tab: this.value
      }
    });
    this.parentElement?.dispatchEvent(event);
  }

  /**
   * Calculates the width needed to display the given text content
   * @private
   * @param {string} textContent - The text content to measure
   * @returns {number} The calculated width in pixels, constrained between min and max values
   */
  private calculateTextWidth(textContent: string) {
    // NOTE: minWidth and maxWidth should be same value as
    // --ids-tab-editor-input-max-width and --ids-tab-editor-input-min-width
    const minWidth = 60;
    const maxWidth = 190;

    const span = document.createElement('span');
    span.style.visibility = 'hidden';
    span.style.position = 'absolute';
    span.style.whiteSpace = 'pre';
    span.textContent = textContent;
    document.body.appendChild(span);
    const width = Math.min(Math.max(span.offsetWidth + 30, minWidth), maxWidth);
    span.remove();
    return width;
  }

  /**
   * Exits inline editing mode for the tab name.
   * Restores the original text display and removes edit controls.
   * @param {boolean} updateLabel - Whether to save the edited value or discard changes
   * @returns {void}
   */
  private endEdit(updateLabel: boolean) {
    this.toggleAttribute(attributes.EDITING);
    const text = this?.shadowRoot?.querySelector('ids-text') as IdsText;
    const input = this?.shadowRoot?.querySelector('.tab-name-editor') as IdsInput;
    if (!text || !input) return;

    if (updateLabel && input.value) {
      text?.shadowRoot?.querySelector('span.ids-text')?.setAttribute('data-text', `"${input.value}"`);
      this.label = input.value;
      text.textContent = input.value;
      if (this.firstChild && this.firstChild.nodeName === '#text') {
        this.firstChild.textContent = input.value;
      }
    }

    text.toggleAttribute(attributes.HIDDEN);
    input.remove();
    this.updateNameBtn.remove();
    this.cancelNameBtn.remove();

    const dismissisedBtn = this.shadowRoot?.querySelector('.ids-tab .ids-tab-trigger-container slot[name="close"]') as HTMLElement;
    if (dismissisedBtn) {
      dismissisedBtn.toggleAttribute(attributes.HIDDEN);
    }

    input.offEvent('input', input);

    const event = new CustomEvent('tab.endedit', {
      bubbles: true,
      detail: {
        elem: this,
        value: input.value,
        tab: this.value
      }
    });
    this.parentElement?.dispatchEvent(event);
  }

  /**
   * @readonly
   * @returns {HTMLElement} the inner popup menu
   */
  get moreOptionsBtn(): any {
    return this.shadowRoot?.querySelector('.more-options-btn');
  }

  /**
   * @readonly
   * @returns {HTMLElement} the inner popup more options menu
   */
  get menu(): any {
    return this.shadowRoot?.querySelector('ids-popup-menu.more-options');
  }

  /**
   * @readonly
   * @returns {HTMLElement} the inner popup more options menu
   */
  get tabTextElement(): IdsText | null {
    return this?.shadowRoot?.querySelector('ids-text') as IdsText;
  }

  /**
   * Refresh component's bindings after render
   */
  #afterConnectedCallback() {
    this.offEvent('slotchange');

    // When any of this item's slots change,
    // refresh the text content so that
    // ids-text::part(text):after can access
    // this for bold sizing fix

    this.onEvent('slotchange', this.container, () => {
      this.#setDataTextForBoldFix();
      this.setAttribute(htmlAttributes.ARIA_LABEL, this.#getReadableAriaLabel());
    });

    this.#setDataTextForBoldFix();
    this.selected = this.hasAttribute(attributes.SELECTED);
    this.#selectSubTab();
  }

  /**
   * @param {boolean} isEditable true if this Tab should be editable
   */
  set editable(isEditable: boolean | string) {
    if (stringToBool(isEditable)) {
      this.setAttribute(attributes.EDITABLE, '');
      this.container?.classList.add(attributes.EDITABLE);
      this.setAttribute(attributes.DIRTY_TRACKER, 'true');
      if (this.tabTextElement) {
        this.tabTextElement.tooltip = 'true';
      }
    } else {
      this.removeAttribute(attributes.EDITABLE);
      this.removeAttribute(attributes.DIRTY_TRACKER);
      this.container?.classList.remove(attributes.EDITABLE);
    }
  }

  /**
   * @returns {boolean} true if this Tab is editable
   */
  get editable(): boolean {
    return this.hasAttribute(attributes.EDITABLE);
  }

  get editing(): boolean {
    return this.hasAttribute(attributes.EDITING);
  }

  /**
   * @param {boolean} isActionable true if this Tab should be displayed as an "action" (small, low padding, no flex)
   */
  set actionable(isActionable: boolean | string) {
    if (stringToBool(isActionable)) {
      this.setAttribute(attributes.ACTIONABLE, '');
      this.container?.classList.add(attributes.ACTIONABLE);
      if (this.value) this.container?.classList.add(this.value);
    } else {
      this.removeAttribute(attributes.ACTIONABLE);
      this.container?.classList.remove(attributes.ACTIONABLE);
      if (this.value) this.container?.classList.remove(this.value);
    }
  }

  /**
   * @returns {boolean} true if this Tab should be displayed as an "action"
   */
  get actionable(): boolean {
    return this.hasAttribute(attributes.ACTIONABLE);
  }

  /**
   * @param {boolean} isDismissible true if this Tab should contain an "X" icon used for dismissal
   */
  set dismissible(isDismissible: boolean | string) {
    if (stringToBool(isDismissible)) {
      this.setAttribute(attributes.DISMISSIBLE, '');
      this.container?.classList.add(attributes.DISMISSIBLE);
      this.insertAdjacentHTML('beforeend', this.buttonTemplates());
    } else {
      this.removeAttribute(attributes.DISMISSIBLE);
      this.container?.classList.remove(attributes.DISMISSIBLE);
      this.dismissibleBtnEl.remove();
    }
  }

  /**
   * @returns {boolean} true if this Tab should contain an "X" icon used for dismissal
   */
  get dismissible(): boolean {
    return this.hasAttribute(attributes.DISMISSIBLE);
  }

  /**
   * Provides a reference to a close button, if applicable
   * @returns {HTMLElement | null} IdsTriggerButton
   */
  get dismissibleBtnEl(): any {
    return this.querySelector('ids-trigger-button[slot="close"]');
  }

  /**
   * Provides a reference to a close button, if applicable
   * @returns {HTMLElement | null} IdsTriggerButton
   */
  get updateNameBtn(): any {
    return this.querySelector('ids-trigger-button[slot="update"]');
  }

  /**
   * Provides a reference to a close button, if applicable
   * @returns {HTMLElement | null} IdsTriggerButton
   */
  get cancelNameBtn(): any {
    return this.querySelector('ids-trigger-button[slot="cancel"]');
  }

  /**
   * Provides a reference to separator if applicable
   * @returns {IdsSeparator | null} IdsSeparator
   */
  get separator(): IdsSeparator | null {
    return this.shadowRoot?.querySelector<IdsSeparator>('ids-separator') || null;
  }

  /**
   * Provides a reference to badge if applicable
   * @returns {IdsBadge | null} IdsBadge
   */
  get badge(): IdsBadge | null {
    return this.shadowRoot?.querySelector<IdsBadge>('ids-badge') || null;
  }

  /**
   * @param {boolean} isDisabled true if the tab should become disabled
   */
  set disabled(isDisabled: boolean) {
    const newValue = stringToBool(isDisabled);
    if (newValue) {
      this.setAttribute(attributes.DISABLED, '');
      this.setAttribute(htmlAttributes.ARIA_DISABLED, 'true');
      this.container?.classList.add(attributes.DISABLED);
      if (this.dismissibleBtnEl) {
        this.dismissibleBtnEl.disabled = true;
      }
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.removeAttribute(htmlAttributes.ARIA_DISABLED);
      this.container?.classList.remove(attributes.DISABLED);
      if (this.dismissibleBtnEl) {
        this.dismissibleBtnEl.disabled = false;
      }
    }
    this.updateTabContentDisabled(newValue);
  }

  /**
   * @returns {boolean} true if this tab is disabled
   */
  get disabled(): boolean {
    return this.hasAttribute(attributes.DISABLED);
  }

  /**
   * Updates the disabled state of the corresponding tab content
   * @param {boolean} isDisabled - Whether the tab content should be disabled
   * @private
   */
  private updateTabContentDisabled(isDisabled: boolean): void {
    if (!this.value) return;

    // First try to find within tabs-context, then search globally
    const tabsContainer = this.closest('ids-tabs-context');
    let tabContent: IdsTabContent | null = null;

    if (tabsContainer) {
      tabContent = tabsContainer.querySelector<IdsTabContent>(`ids-tab-content[value="${this.value}"]`);
    } else {
      tabContent = document.querySelector<IdsTabContent>(`ids-tab-content[value="${this.value}"]`);
    }

    if (tabContent) {
      tabContent.setAttribute(attributes.DISABLED, `${isDisabled}`);
    }
  }

  /**
   * @param {boolean} isSelected Whether or not this tab is selected.
   */
  private previousSelectedState?: boolean;

  set selected(isSelected: boolean) {
    const newValue = stringToBool(isSelected);

    if (this.previousSelectedState === newValue && newValue === true) {
      return;
    }
    this.previousSelectedState = newValue;

    if (!newValue) {
      this.removeAttribute(attributes.SELECTED);
      this.container?.classList.remove(attributes.SELECTED);
      this.container?.querySelector('ids-text')?.removeAttribute?.(attributes.FONT_WEIGHT);
      this.setAttribute(htmlAttributes.TABINDEX, '-1');
      if (this.separator) this.separator.hidden = true;
      if (this.badge) this.badge.color = 'inactive';
      if (this.moreOptionsBtn) this.moreOptionsBtn.hidden = true;
    } else {
      this.setAttribute(attributes.SELECTED, '');
      this.container?.children?.[0]?.setAttribute?.(attributes.FONT_WEIGHT, 'semi-bold');
      this.container?.classList.add(attributes.SELECTED);
      this.setAttribute(htmlAttributes.TABINDEX, '0');
      this.#select(newValue);
      if (this.separator && this.dismissible) this.separator.hidden = false;
      if (this.moreOptionsBtn) this.moreOptionsBtn.hidden = false;
      if (this.badge) this.badge.color = 'info';
    }
    this.setAttribute(htmlAttributes.ARIA_SELECTED, `${newValue}`);
    this.container?.removeAttribute(htmlAttributes.TABINDEX);
  }

  /**
   * @returns {boolean} isSelected Whether or not this tab is selected.
   */
  get selected(): boolean {
    return this.hasAttribute(attributes.SELECTED);
  }

  set notificationCount(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.NOTIFICATION_COUNT, value);
      this.container?.classList.add(attributes.NOTIFICATION_COUNT);
    } else {
      this.removeAttribute(attributes.NOTIFICATION_COUNT);
      this.container?.classList.remove(attributes.NOTIFICATION_COUNT);
    }
  }

  /**
   * @returns {string | number} the number of notification counts.
   */
  get notificationCount(): string | number {
    return this.getAttribute(attributes.NOTIFICATION_COUNT) === null ? 0
      : Number(this.getAttribute(attributes.NOTIFICATION_COUNT));
  }

  /**
   * Triggers the `tabselect` event
   * @param {boolean} isSelected true if the tab has been selected
   */
  #select(isSelected: boolean): void {
    if (this.actionable) return;
    this.triggerEvent('tabselect', this, {
      bubbles: true,
      detail: {
        selected: isSelected
      }
    });
  }

  #selectSubTab() {
    const tab: any = this;
    const tabEl = tab.shadowRoot?.querySelector('.ids-tab');
    if (!tabEl) return;

    const hasSubTabs = tabEl.classList.contains('has-sub-tabs');
    if (!hasSubTabs) return;

    const subTab = tab.shadowRoot?.querySelector('ids-sub-tab');
    if (subTab) {
      this.triggerEvent('tabselect', this, {
        bubbles: true,
        detail: {
          selected: true,
          value: subTab.value
        }
      });
    }
  }

  /** @param {string} value The value which becomes selected by ids-tabs component */
  set value(value: string | null) {
    if (value === this.getAttribute(attributes.VALUE)) return;

    this.setAttribute(attributes.VALUE, String(value));
    this.setAriaControls();
    this.#valueChange(value);
  }

  /** @returns {string} value The value which becomes selected by ids-tabs component */
  get value(): string | null {
    return this.getAttribute(attributes.VALUE);
  }

  /**
   * @param {string} value The value which becomes selected by ids-tabs component
   */
  #valueChange(value: string | null) {
    this.triggerEvent('tabvaluechange', this, {
      bubbles: true,
      detail: { value: `${value}` }
    });
  }

  /**
   * Disable the error icon that will appear on a tab upon validation
   * @param {boolean|string} value If true, the error icon will be disabled
   */
  set disableError(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.DISABLE_ERROR, val.toString());
    } else {
      this.removeAttribute(attributes.DISABLE_ERROR);
    }
  }

  get disableError(): boolean {
    return stringToBool(this.getAttribute(attributes.DISABLE_ERROR));
  }

  set hasError(value: boolean | string) {
    const val = stringToBool(value);
    if (val && !this.disableError) {
      this.setAttribute(attributes.HAS_ERROR, val.toString());
    } else {
      this.removeAttribute(attributes.HAS_ERROR);
    }
  }

  get hasError(): boolean {
    return stringToBool(this.getAttribute(attributes.HAS_ERROR));
  }

  /**
   * Set the label text for the tab
   * @param {string | null} value The label text
   */
  set label(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LABEL, value);
    } else {
      this.removeAttribute(attributes.LABEL);
    }
  }

  /**
   * @returns {string | null} The label text
   */
  get label(): string | null {
    return (this.getAttribute(attributes.LABEL) || this.getAttribute(htmlAttributes.ARIA_LABEL))?.trim() || null;
  }

  /**
   * Set the hidden state of the tab
   * @param {boolean|string} value If true, the tab will be hidden
   */
  set hidden(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.HIDDEN, '');

      // Find and hide the corresponding tab content
      this.#hideTabContent();
    } else {
      this.removeAttribute(attributes.HIDDEN);

      // Show the corresponding tab content if this tab is selected
      if (this.selected) {
        this.#showTabContent();
      }
    }
  }

  /**
   * @returns {boolean} true if this tab is hidden
   */
  get hidden(): boolean {
    return this.hasAttribute(attributes.HIDDEN);
  }

  /**
   * Hide the corresponding tab content
   * @private
   */
  #hideTabContent(): void {
    const tabValue = this.value;
    if (!tabValue) return;

    // First try to find within tabs-context, then search globally
    const tabsContainer = this.closest('ids-tabs-context');
    let tabContent: any = null;
    if (tabsContainer) {
      tabContent = tabsContainer.querySelector(`ids-tab-content[value="${tabValue}"]`);
    } else {
      tabContent = document.querySelector(`ids-tab-content[value="${tabValue}"]`);
    }

    if (tabContent) {
      tabContent.setAttribute(attributes.HIDDEN, '');
      tabContent.removeAttribute(attributes.ACTIVE);
    }
  }

  /**
   * Show the corresponding tab content
   * @private
   */
  #showTabContent(): void {
    const tabValue = this.value;
    if (!tabValue) return;

    // First try to find within tabs-context, then search globally
    const tabsContainer = this.closest('ids-tabs-context');
    let tabContent: any = null;

    if (tabsContainer) {
      tabContent = tabsContainer.querySelector(`ids-tab-content[value="${tabValue}"]`);
    } else {
      tabContent = document.querySelector(`ids-tab-content[value="${tabValue}"]`);
    }

    if (tabContent) {
      tabContent.removeAttribute(attributes.HIDDEN);
      tabContent.setAttribute(attributes.ACTIVE, '');
    }
  }

  /** @returns {string} value The number of items represented in the tab (may or may not apply) */
  get count(): string | null {
    return this.getAttribute(attributes.COUNT);
  }

  /**
   * @param {string} value The number of items represented in the tab (may or may not apply)
   */
  set count(value: string | null) {
    if (value === '') {
      if (this.hasAttribute(attributes.COUNT)) {
        this.removeAttribute(attributes.COUNT);
      }
      this.container?.classList.remove(attributes.COUNT);
      return;
    }

    if (Number.isNaN(Number(value))) {
      return;
    }

    this.container?.classList.add(attributes.COUNT);

    if (this.getAttribute(attributes.COUNT) !== value) {
      this.setAttribute(attributes.COUNT, String(value));
    }
  }

  set maxWidth(value: string) {
    if (isNaN(Number.parseInt(value))) return; // eslint-disable-line

    // Following IDS Legacy Default Tab Size for Min / Max Width
    const defaultTabSize = this.parentElement?.getAttribute('color-variant') ? 120 : 80;
    const tabEl = this.shadowRoot?.querySelector<HTMLElement>('.ids-tab');
    const textEl = this.shadowRoot?.querySelector('ids-text')?.shadowRoot?.querySelector<HTMLElement>('.ids-text');
    if (tabEl) {
      this.style.maxWidth = stringToNumber(value) < defaultTabSize ? `${defaultTabSize}px` : `${value}px`;
      tabEl.style.maxWidth = stringToNumber(value) < defaultTabSize ? `${defaultTabSize}px` : `${value}px`;
    }
    if (textEl) textEl.style.maxWidth = this.dismissible && !this.disabled ? `${stringToNumber(value) - 40}px` : `${value}px`;
  }

  get maxWidth() {
    return this.maxWidth;
  }

  set minWidth(value: string) {
    if (isNaN(Number.parseInt(value))) return; // eslint-disable-line
    // Following IDS Legacy Default Tab Size for Min / Max Width
    const defaultTabSize = this.parentElement?.getAttribute('color-variant') ? 120 : 80;
    if (this.hasAttribute(attributes.MAX_WIDTH)
      && stringToNumber(value) > stringToNumber(this.getAttribute(attributes.MAX_WIDTH))) {
      value = this.getAttribute(attributes.MAX_WIDTH) || defaultTabSize.toString();
    }

    const tabEl = this.shadowRoot?.querySelector<HTMLElement>('.ids-tab');
    if (tabEl) {
      this.style.minWidth = stringToNumber(value) < defaultTabSize ? `${defaultTabSize}px` : `${value}px`;
      tabEl.style.minWidth = stringToNumber(value) < defaultTabSize ? `${defaultTabSize}px` : `${value}px`;
    }
  }

  get minWidth() {
    return this.minWidth;
  }

  /**
   * sets aria readable label by
   * grabbing all ids-text nodes in order
   * they appear in the DOM
   * @returns {string} aria-label content
   */
  #getReadableAriaLabel(): string {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const idsTextEls = [...this.container?.querySelectorAll('ids-text') ?? []];
    return idsTextEls.map((textEl) => {
      const slotNode = textEl.querySelector('slot')?.assignedNodes?.()?.[0];
      return (slotNode?.textContent || textEl.textContent)?.trim();
    }).join(' ');
  }

  /**
   * Sets the data-text of nested ids-text to it's slot
   * text content. Fixes issue with bold moving around
   * when we edit content.
   */
  #setDataTextForBoldFix = () => {
    if (this.count) {
      return;
    }
    const idsText = this.container?.querySelector<IdsText>('ids-text');
    const slotNode = idsText?.querySelector('slot') || idsText?.shadowRoot?.querySelector('slot');
    const slotContent = slotNode?.assignedNodes?.()?.[0];

    if (slotContent && idsText) {
      idsText?.container?.setAttribute('data-text', `${slotContent.textContent?.trim()}`);
    }
  };

  /**
   * Sets aria-controls attribute to point to the corresponding tab panel
   * @private
   */
  private setAriaControls(): void {
    if (this.value) {
      this.setAttribute(htmlAttributes.ARIA_CONTROLS, `tabpanel-${this.value}`);
    }
  }

  /**
   * Sets/removes CSS classes on this tab that control "swappable" display
   */
  #detectSwappable(): void {
    const swappableParent = this.parentElement && this.parentElement.tagName === 'IDS-SWAPPABLE-ITEM';
    this.container?.classList[swappableParent ? 'add' : 'remove']('swappable');
  }

  /**
   * Dismisses this tab, if possible
   */
  dismiss(): void {
    if (this.dismissible) {
      if (!this.triggerVetoableEvent('beforetabremove')) return;

      this.triggerEvent('tabremove', this, {
        bubbles: true,
        detail: {
          elem: this,
          value: this.value,
          subTabValue: this.tabGroup.subTabValue
        }
      });
    }
  }

  /**
   * Handles click events for tab action buttons.
   * Executes appropriate actions based on the button type (close, update, cancel).
   * @param {string} btnName - The name of the button that was clicked
   * @returns {void}
   */
  onTabButtonClick(btnName: string) {
    if (btnName === 'close') {
      this.dismiss();
    }

    if (btnName === 'update') {
      this.endEdit(true);
    }

    if (btnName === 'cancel') {
      this.endEdit(false);
    }
  }

  /**
   * Causes the tab to become focused
   */
  focus(): void {
    if (!this.disabled) {
      super.focus();
    }
  }

  onColorVariantRefresh(): void {
    const closeBtn = this.dismissibleBtnEl;
    if (closeBtn) {
      const target = this.#getDismissibleVariant();
      closeBtn.colorVariant = target;
    }
  }
}
